import React, { FunctionComponent, useState } from 'react';
import { FormGroup, Col, Label, Button } from 'reactstrap';
import FormControl from 'components/utils/FormControl';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';
import { CrossIxConnection, ApiResponse } from 'constants/api';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { pick } from 'lodash';
import { object, string } from 'yup';
import { createIpv4AddressOrRangeValidation, createIpv6AddressOrRangeValidation } from 'validation';
import { transformNullToBlank } from 'utils/commonUtils';
import FormModal from './FormModal';

interface InProps {
    crossIxConnection: CrossIxConnection;
}

interface EnhanceProps {
    updateCrossIxConnectionBinding: ApiDataBinding<ApiResponse<CrossIxConnection>>;
}

type Props = InProps & EnhanceProps & WithTranslation;

const enhance = compose<Props, InProps>(
    withApiData({
        updateCrossIxConnectionBinding: 'putCrossIxConnection',
    }),
    withTranslation()
);

type FormValues = Pick<
    CrossIxConnection,
    | 'source_exchange_name'
    | 'destination_exchange_name'
    | 'target_vlan_name'
    | 'source_exchange_customer_link_vlan_xml_id'
    | 'source_exchange_transport_link_vlan_xml_id'
    | 'destination_exchange_customer_link_vlan_xml_id'
    | 'destination_exchange_link_vlan_ipv4_address'
    | 'destination_exchange_link_vlan_ipv6_address'
    | 'note'
>;

const CrossIxConnectionEditModal: FunctionComponent<Props> = ({
    crossIxConnection,
    updateCrossIxConnectionBinding,
    t,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }

    function onSubmit(values: FormValues) {
        return updateCrossIxConnectionBinding.perform({ uuid: crossIxConnection.uuid }, values);
    }

    return (
        <React.Fragment>
            <FormModal
                header={t('crossIxConnectionEditModal.title')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={transformNullToBlank(
                    pick(
                        crossIxConnection,
                        'source_exchange_name',
                        'destination_exchange_name',
                        'target_vlan_name',
                        'source_exchange_customer_link_vlan_xml_id',
                        'source_exchange_transport_link_vlan_xml_id',
                        'destination_exchange_customer_link_vlan_xml_id',
                        'destination_exchange_link_vlan_ipv4_address',
                        'destination_exchange_link_vlan_ipv6_address',
                        'note'
                    )
                )}
                validationSchema={object().shape({
                    source_exchange_name: string().required(t('validation.requiredField')),
                    destination_exchange_name: string().required(t('validation.requiredField')),
                    destination_exchange_link_vlan_ipv4_address: createIpv4AddressOrRangeValidation(t),
                    destination_exchange_link_vlan_ipv6_address: createIpv6AddressOrRangeValidation(t),
                })}
                onSubmit={onSubmit}
                successMessage={t('crossIxConnectionEditModal.successMessage')}
            >
                {({ errors, touched }) => (
                    <React.Fragment>
                        {
                            <>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="source_exchange_name">
                                            {t('crossIxConnection.sourceExchangeName')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="source_exchange_name"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="destination_exchange_name">
                                            {t('crossIxConnection.destinationExchangeName')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="destination_exchange_name"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="target_vlan_name">
                                            {t('crossIxConnection.targetVlanName')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="target_vlan_name"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="source_exchange_customer_link_vlan_xml_id">
                                            {t('crossIxConnection.sourceExchangeCustomerLinkVlanXmlId')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="source_exchange_customer_link_vlan_xml_id"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="source_exchange_transport_link_vlan_xml_id">
                                            {t('crossIxConnection.sourceExchangeTransportLinkVlanXmlId')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="source_exchange_transport_link_vlan_xml_id"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="destination_exchange_customer_link_vlan_xml_id">
                                            {t('crossIxConnection.destinationExchangeCustomerLinkVlanXmlId')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="destination_exchange_customer_link_vlan_xml_id"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="destination_exchange_link_vlan_ipv4_address">
                                            {t('crossIxConnection.destinationExchangeLinkVlanIpv4Address')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="destination_exchange_link_vlan_ipv4_address"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="destination_exchange_link_vlan_ipv6_address">
                                            {t('crossIxConnection.destinationExchangeLinkVlanIpv6Address')}
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            type="text"
                                            name="destination_exchange_link_vlan_ipv6_address"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Col md={3}>
                                        <Label htmlFor="note">{t('crossIxConnection.note')}</Label>
                                    </Col>
                                    <Col md={9}>
                                        <FormControl
                                            component="textarea"
                                            name="note"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>
                            </>
                        }
                    </React.Fragment>
                )}
            </FormModal>
            <Button data-test="edit-cross-ix-connection-btn" color="light" onClick={toggle}>
                {t('crossIxConnectionEditModal.editBtn')}
            </Button>
        </React.Fragment>
    );
};

export default enhance(CrossIxConnectionEditModal);
